import React from "react";
import {
    DeleteOutlined,
    EditOutlined,
    ProfileOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, notification, Popconfirm } from "antd";
import HTTP from "../../services/httpService";
import { getUserRole } from "../../services/util";
import {
    AlarmNameCell,
    ChipList,
    ContactList,
    CreatedByCell,
    PriorityTag,
    alarmTypeChipIcon,
    emailListIcon,
    hotelChipIcon,
    phoneListIcon,
} from "./tableCells";

// ─── Constants ────────────────────────────────────────────────────────────────

const ADMIN_ROLES = ["Super Admin", "Admin"];

// ─── Column factory ───────────────────────────────────────────────────────────

const column = ({
    openAlarmModal,
    getAlarms,
    hotelListRes: hotels,
    alarmPriority,
    alarmType,
    showHistory,
}) => {
    const getPriorityLabel = (value) =>
        alarmPriority.find((e) => e.value == value)?.name ?? "";

    const getAlarmTypeLabel = (key) =>
        alarmType.find((e) => e.key == key)?.title ?? "";

    const deleteAlarm = async (id, staff_id) => {
        const userRole = getUserRole(localStorage.getItem("userRoleId"));
        if (!ADMIN_ROLES.includes(userRole)) {
            return notification.error({
                message: "You are not allowed to perform this action.",
            });
        }
        const { success, message } = await HTTP("/alarms/deleteAlarm", {
            id,
            staff_id,
        });
        if (success === 0) {
            notification.error({ message });
        } else {
            notification.info({ message });
            getAlarms();
        }
    };

    return [
        {
            title: "Alarm Name",
            dataIndex: "alarm_name",
            key: "alarm_name",
            sorter: (a, b) => a.alarm_name.localeCompare(b.alarm_name),
            width: "12%",
            render: (val) => <AlarmNameCell value={val} />,
        },
        {
            title: "Hotel Names",
            dataIndex: "alarm_hotel_ids",
            key: "alarm_hotel_ids",
            width: "18%",
            render: (ids) => {
                const hotelNames = ids?.map(
                    (id) =>
                        hotels?.data?.find((h) => h.hotel_id == id)?.hotel_name,
                );

                return (
                    <ChipList
                        items={hotelNames}
                        icon={hotelChipIcon}
                        bg="var(--color-primary-light)"
                        border="var(--color-border)"
                        color="var(--color-primary)"
                    />
                );
            },
        },
        {
            title: "Emails",
            dataIndex: "alarm_email_ids",
            key: "alarm_email_ids",
            width: "20%",
            render: (emails) => (
                <ContactList items={emails} icon={emailListIcon} />
            ),
        },
        {
            title: "Phone Numbers",
            dataIndex: "alarm_phone_numbers",
            key: "alarm_phone_numbers",
            width: "16%",
            render: (phones) => (
                <ContactList items={phones} icon={phoneListIcon} />
            ),
        },
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            sorter: (a, b) => a.priority - b.priority,
            width: "8%",
            render: (val) => <PriorityTag label={getPriorityLabel(val)} />,
        },
        {
            title: "Alarm Types",
            dataIndex: "alarm_type",
            key: "alarm_type",
            width: "14%",
            render: (types) => {
                const alarmTypes = types?.map((t) => getAlarmTypeLabel(t));

                return (
                    <ChipList
                        items={alarmTypes}
                        icon={alarmTypeChipIcon}
                        bg="var(--color-accent-light)"
                        border="var(--color-border)"
                        color="var(--color-accent)"
                    />
                );
            },
        },
        {
            title: "Created By",
            key: "created_by",
            width: "10%",
            render: (_, record) => (
                <CreatedByCell
                    firstName={record.staff_first_name}
                    lastName={record.staff_last_name}
                />
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Popconfirm
                        title="Delete this alarm?"
                        description="This action cannot be undone."
                        onConfirm={() =>
                            deleteAlarm(record.id, record.staff_id)
                        }
                        icon={
                            <QuestionCircleOutlined
                                style={{ color: "var(--color-error)" }}
                            />
                        }
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            size="small"
                            icon={
                                <DeleteOutlined
                                    style={{ color: "var(--color-error)" }}
                                />
                            }
                            style={{
                                background: "var(--color-error-light)",
                                border: "1px solid rgba(244, 63, 94, 0.2)",
                                borderRadius: 7,
                                width: 30,
                                height: 30,
                            }}
                        />
                    </Popconfirm>

                    <Button
                        type="text"
                        size="small"
                        icon={
                            <EditOutlined
                                style={{ color: "var(--color-primary)" }}
                            />
                        }
                        onClick={() => openAlarmModal(record)}
                        style={{
                            background: "var(--color-bg-elevated)",
                            border: "1px solid var(--color-border)",
                            borderRadius: 7,
                            width: 30,
                            height: 30,
                        }}
                    />

                    <Button
                        type="text"
                        size="small"
                        icon={
                            <ProfileOutlined
                                style={{ color: "var(--color-accent)" }}
                                id={record.id}
                            />
                        }
                        onClick={showHistory}
                        style={{
                            background: "var(--color-accent-light)",
                            border: "1px solid rgba(20, 184, 166, 0.2)",
                            borderRadius: 7,
                            width: 30,
                            height: 30,
                        }}
                    />
                </div>
            ),
        },
    ];
};

export default column;
